const CHAT_VIEW_SELECTOR = '.raid-control-workspace.mobile.view-chat';
const CHAT_INPUT_SELECTOR = '.raid-control-room.mobile .raid-control-composer textarea';
const INTERNAL_REFOCUS_SELECTOR = [
  '.raid-control-mention-suggestions button',
  '.raid-control-composer-mentions button',
].join(',');

let installedCleanup = null;

function isChatInput(element) {
  return element instanceof HTMLElement && element.matches(CHAT_INPUT_SELECTOR);
}

function isInternalRefocusControl(element) {
  return element instanceof Element
    && Boolean(element.closest(CHAT_VIEW_SELECTOR))
    && Boolean(element.closest(INTERNAL_REFOCUS_SELECTOR));
}

export function installMobileChatComposerFocusGuard() {
  if (installedCleanup || typeof window === 'undefined' || typeof document === 'undefined') {
    return installedCleanup || (() => {});
  }

  let internalRefocusPending = false;

  const handlePointerDown = (event) => {
    if (!isChatInput(document.activeElement) || !isInternalRefocusControl(event.target)) return;

    // Autocomplete-style mention controls must not take focus from the textarea.
    // On Galaxy, letting the button become active starts a real keyboard hide/pan
    // before React focuses the textarea again, which can move the whole workspace.
    internalRefocusPending = true;
    if (event.cancelable) event.preventDefault();
  };

  const handleFocusOut = (event) => {
    if (!isChatInput(event.target)) return;
    const nextTargetIsInternal = isInternalRefocusControl(event.relatedTarget);
    if (!internalRefocusPending && !nextTargetIsInternal) return;

    // Fallback for keyboard activation or browsers that still transfer focus.
    // Keep the transfer invisible to the viewport recovery state machine.
    internalRefocusPending = true;
    event.stopImmediatePropagation();
  };

  const handleFocusIn = (event) => {
    if (!internalRefocusPending || !isChatInput(event.target)) return;

    internalRefocusPending = false;
    event.stopImmediatePropagation();
  };

  const handlePointerDownReset = (event) => {
    if (isInternalRefocusControl(event.target)) return;
    if (!isChatInput(event.target)) internalRefocusPending = false;
  };

  const handleClick = (event) => {
    if (!internalRefocusPending || !isInternalRefocusControl(event.target)) return;
    // The control's React onClick still performs the mention action. Clear the
    // guard after that click without forcing another focus transition here.
    window.requestAnimationFrame(() => {
      if (isChatInput(document.activeElement)) internalRefocusPending = false;
    });
  };

  document.addEventListener('pointerdown', handlePointerDown, true);
  document.addEventListener('pointerdown', handlePointerDownReset, false);
  document.addEventListener('click', handleClick, true);
  document.addEventListener('focusout', handleFocusOut, true);
  document.addEventListener('focusin', handleFocusIn, true);

  const cleanup = () => {
    document.removeEventListener('pointerdown', handlePointerDown, true);
    document.removeEventListener('pointerdown', handlePointerDownReset, false);
    document.removeEventListener('click', handleClick, true);
    document.removeEventListener('focusout', handleFocusOut, true);
    document.removeEventListener('focusin', handleFocusIn, true);
    internalRefocusPending = false;
    installedCleanup = null;
  };

  installedCleanup = cleanup;
  if (import.meta.hot) import.meta.hot.dispose(cleanup);
  return cleanup;
}
